// =====================================================================================
// INVRT Radar - web console - application shell (story P2-UI: the DS Nav Rail rebuild)
//
// Mark rejected the previous shell in writing; this shell is the canonical INVRT Design
// System 2.8.0 app shell (templates/app-shell) with the Nav Rail (components/Nav/Rail.jsx)
// ported faithfully: .inv-rail head/body/foot, grouped .inv-nav-item targets styled by the
// vendored DS stylesheet (src/design-system/styles.css), 232px open / 64px minimised, one
// explicit collapse control. Below the DS's 900px rail breakpoint the shell switches to
// the TabBar pattern (components/Nav/TabBar.jsx): at most five targets, every one on the
// 44px touch floor.
//
// Navigation carries ONLY surfaces that are actually built - an unbuilt page is absent,
// never a link to a stub. The user area is Ask Radar, Find people, Contacts, Site
// visitors, Suppression, Account and, when the organisation holds the entitlement,
// HIGH-INTENT. The operator area is Catalogue, Access and Organisations - ONE SCREEN PER
// JOB on the rail (the console is no longer one endless page) - and Billing is GONE from
// the console entirely (Mark's money ruling: no HIGH-INTENT money machinery while clients
// are INVRT-billed). The operator entry appears only when profiles.is_super_admin is
// true, and even then the server is the thing enforcing it.
//
// The compact-header brand keeps the sanctioned amendment (SPRINT_DECISIONS_2026-09-22,
// item 2): "Radar" renders in text-accent-700 (#075851 on white = 8.32:1).
// =====================================================================================

import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BadgePoundSterling,
  Boxes,
  BrainCircuit,
  Building2,
  Gauge,
  Globe,
  KeyRound,
  LogOut,
  Menu,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { useBalance } from "@/hooks/useApi";
import { hasModuleEntitlement, useEntitlements } from "@/hooks/useEntitlements";
import { formatCredits } from "@/lib/format";
import { cn } from "@/lib/utils";
import { OrgSwitcher } from "./OrgSwitcher";
import { InvrtBrand } from "./InvrtBrand";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Search;
  end?: boolean;
}

/** The user area. One group on the rail, in Mark's order. */
const WORKSPACE_NAV: NavItem[] = [
  { to: "/", label: "Ask RADAR", icon: MessageSquare, end: true },
  { to: "/prospecting", label: "Find people", icon: Search },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/sites", label: "Site visitors", icon: Globe },
  { to: "/suppression", label: "Suppression", icon: SlidersHorizontal },
  { to: "/account", label: "Account", icon: BadgePoundSterling },
];

const HIGH_INTENT_NAV: NavItem = { to: "/high-intent", label: "HIGH-INTENT", icon: BrainCircuit };

/** The operator area: one screen per job. Billing is removed from the console entirely;
 * Consumption (story O10) is the read-only metering and price book view. */
const OPERATOR_NAV: NavItem[] = [
  { to: "/operator/catalogue", label: "Catalogue", icon: Boxes },
  { to: "/operator/access", label: "Access", icon: KeyRound },
  { to: "/operator/organisations", label: "Organisations", icon: Building2 },
  { to: "/operator/consumption", label: "Consumption", icon: Gauge },
];

function RailItem({ item, minimised }: { item: NavItem; minimised: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      title={minimised ? item.label : undefined}
      className="inv-nav-item min-h-11"
    >
      <item.icon aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function BalanceChip() {
  const { data, isLoading, isError } = useBalance();

  return (
    <div
      className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5"
      aria-live="polite"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">Balance</span>
      <span className="text-sm font-semibold text-ink">
        {isLoading ? "…" : isError ? "unavailable" : formatCredits(data?.balance)}
      </span>
    </div>
  );
}

export function AppShell() {
  const { signOut, session, isSuperAdmin, orgId } = useSession();
  const entitlements = useEntitlements();
  const [userMinimised, setUserMinimised] = useState(false);
  // The DS's own medium band (901-1279px) is the icon rail: styles.css hides the item
  // labels there, and the canonical Rail.jsx expects product code to drive the compact
  // widths ("fixed in product code; do not re-derive them per screen") so every target
  // keeps its accessible name through `title`. Leaving the band on the full 232px rail
  // rendered seven unnamed links (axe link-name, serious, WCAG 4.1.2) - the B12 walk's
  // deciding RED at 901/1279, repaired here at the state derivation, the named seam.
  const [bandMinimised, setBandMinimised] = useState(false);
  useEffect(() => {
    const band = window.matchMedia("(min-width: 901px) and (max-width: 1279px)");
    const update = () => setBandMinimised(band.matches);
    update();
    band.addEventListener("change", update);
    return () => band.removeEventListener("change", update);
  }, []);
  const minimised = userMinimised || bandMinimised;
  const [navigationOpen, setNavigationOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setNavigationOpen(false); }, [pathname, orgId, session?.user.id, isSuperAdmin]);
  const navigationTrigger = useRef<HTMLButtonElement>(null);
  const closeNavigation = () => {
    setNavigationOpen(false);
    requestAnimationFrame(() => navigationTrigger.current?.focus());
  };

  // Navigation is only a courtesy. The RADAR server verifies the same selected-org
  // entitlement on every E06 adapter request before HIGH-INTENT sees anything.
  const highIntentEntitled = entitlements.isSuccess && !entitlements.isFetching &&
    hasModuleEntitlement(entitlements.data, "high-intent");
  const workspace = highIntentEntitled ? [...WORKSPACE_NAV, HIGH_INTENT_NAV] : WORKSPACE_NAV;

  // The TabBar carries at most five targets (the DS's own law). The fifth slot follows
  // the viewer: staff get the operator console, an entitled member the module, everyone
  // else their account. Nothing is hidden on the desktop rail at any width.
  const fifthSlot: NavItem = isSuperAdmin
    ? { to: "/operator/catalogue", label: "Operator", icon: Boxes }
    : highIntentEntitled
      ? HIGH_INTENT_NAV
      : WORKSPACE_NAV[5]!; // Account
  const tabItems: NavItem[] = [...WORKSPACE_NAV.slice(0, 4), fifthSlot];

  return (
    <div className="inv-app" data-rail={minimised ? "min" : "full"}>
      {/* ---- the DS Nav Rail (components/Nav/Rail.jsx) ---- */}
      <nav className={cn("inv-rail max-[900px]:hidden")} aria-label="Primary">
        <div className="inv-rail-head" style={minimised ? { height: "auto", flexDirection: "column", gap: 0, padding: "var(--space-10)" } : undefined}>
          <InvrtBrand compact={minimised} className="text-ink" productClassName="text-primary" />
          {/* The band's compact state is the DS's own law, not a user choice, so the
              toggle renders only where collapsing has effect (outside the band). */}
          {!bandMinimised && (
          <button
            type="button"
            onClick={() => setUserMinimised((current) => !current)}
            aria-expanded={!minimised}
            aria-label={minimised ? "Expand navigation" : "Collapse navigation"}
            className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-surface-muted hover:text-ink", !minimised && "ml-auto")}
          >
            {minimised
              ? <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
              : <PanelLeftClose className="h-4 w-4" aria-hidden="true" />}
          </button>
          )}
        </div>

        <div className="inv-rail-body">
          <div>
            {!minimised && <div className="inv-nav-group">Workspace</div>}
            {workspace.map((item) => (
              <RailItem key={item.to} item={item} minimised={minimised} />
            ))}
          </div>
          {isSuperAdmin ? (
            <div>
              {!minimised && <div className="inv-nav-group">Operator</div>}
              {OPERATOR_NAV.map((item) => (
                <RailItem key={item.to} item={item} minimised={minimised} />
              ))}
            </div>
          ) : null}
        </div>

        <div className="inv-rail-foot" style={minimised ? { padding: "var(--space-10)" } : undefined}>
          <p className="truncate text-xs text-ink-3">{session?.user.email}</p>
          <button
            type="button"
            onClick={() => void signOut()}
            title={minimised ? "Sign out" : undefined}
            className={cn("mt-2 flex min-h-11 items-center gap-2 text-xs text-ink-3 transition-colors hover:text-ink", minimised && "h-11 w-11 shrink-0 justify-center")}
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            <span className={minimised ? "sr-only" : undefined}>Sign out</span>
          </button>
        </div>
      </nav>

      {/* ---- the DS main column: topbar, page, and the mobile TabBar ---- */}
      <div className="inv-main">
        <header className="inv-topbar max-[900px]:h-auto max-[900px]:min-h-14 max-[900px]:flex-wrap max-[900px]:gap-3 max-[900px]:py-3">
          <div className="min-[901px]:hidden">
            <InvrtBrand cap={16} className="text-ink" productClassName="text-accent-700" />
          </div>
          <Button ref={navigationTrigger} type="button" variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] min-[901px]:hidden" aria-label="More navigation" aria-haspopup="dialog" aria-expanded={navigationOpen} onClick={() => setNavigationOpen(true)}><Menu className="h-5 w-5" aria-hidden="true" /></Button>
          <OrgSwitcher />
          <div className="ml-auto flex items-center gap-3">
            <BalanceChip />
            <button
              type="button"
              onClick={() => void signOut()}
              className="flex min-h-11 items-center gap-2 text-xs text-ink-2 transition-colors hover:text-ink min-[901px]:hidden"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </header>

        <Dialog open={navigationOpen} onClose={closeNavigation} title="Navigation" description="Choose a page available to your current account.">
          <nav aria-label="All pages" className="space-y-1">
            {[...workspace, ...(isSuperAdmin ? OPERATOR_NAV : [])].map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end ?? false} onClick={closeNavigation} className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 text-sm text-ink hover:bg-surface-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"><item.icon className="h-5 w-5" aria-hidden="true" /><span>{item.label}</span></NavLink>
            ))}
          </nav>
        </Dialog>
        <main className="inv-page min-w-0 flex-1 overflow-auto">
          {/* Drafts, mutation results and approvals belong to this user and organisation.
              Keep the switcher mounted for focus, but discard the prior page instance. */}
          <Outlet key={`${session?.user.id ?? "signed-out"}:${orgId ?? "no-organisation"}`} />
        </main>

        {/* ---- the DS TabBar (components/Nav/TabBar.jsx): max five, >=44px each.
             The DS stylesheet reveals it below 900px. ---- */}
        <nav className="inv-tabbar" aria-label="Mobile">
          <div className="inv-tabbar-inner">
            {tabItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className="inv-tab min-h-[2.75rem]"
              >
                <item.icon aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
